import React, { useState } from "react";
import { Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useOutlookSourceSettingsText,
  useOutlookSourceSettingsViewModel,
} from "@onetasklist/shared/sources/outlook";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { NewTaskAgeLimitField } from "./NewTaskAgeLimitField";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface OutlookMailSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const OutlookMailSourceSettings: React.FC<
  OutlookMailSourceSettingsProps
> = ({ editingConnection, onUpdate, sectionOffset }) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const vm = useOutlookSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });
  const text = useOutlookSourceSettingsText(t, "mail");
  const [foldersQuery, setFoldersQuery] = useState("");

  if (!vm.isVisible || !vm.isOutlookMail) {
    return null;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.outlookmail.sectionFolders",
      )}
    >
      <Field orientation="horizontal" label={text.labelText} hint={text.hintText}>
        <SourceMultiTagPicker
          isLoading={vm.isLoading}
          selectedIds={vm.selectedFolderIds}
          query={foldersQuery}
          placeholder={text.notSetText}
          options={vm.folderOptions.map((folder) => ({
            id: folder.id,
            label: folder.label,
          }))}
          onQueryChange={setFoldersQuery}
          onSelectionChange={vm.onSelectFolders}
          minQueryLength={0}
          typeToSearchText={text.hintText}
        />
      </Field>

      <NewTaskAgeLimitField
        editingConnection={editingConnection}
        onUpdate={onUpdate}
      />

      {vm.isLoading && <Text>{text.loadingText}</Text>}
      {vm.hasError && <Text>{text.loadErrorText}</Text>}
    </SettingsSectionCard>
  );
};
